// JWT Token Service for MUU Platform
import { JWT_CONFIG, SECURITY_CONFIG } from '../../config/api';
import {
  JWTTokens,
  TokenPayload,
  RefreshTokenRequest,
  RefreshTokenResponse,
  TokenStorage,
  AuthUser,
} from '../../types/auth';
import { storage } from '../../utils';

/**
 * Token Service Class
 * Handles JWT token management, storage, and validation
 */
class TokenService implements TokenStorage {
  private readonly accessTokenKey = JWT_CONFIG.accessTokenKey;
  private readonly refreshTokenKey = JWT_CONFIG.refreshTokenKey;
  private refreshPromise: Promise<boolean> | null = null;

  /**
   * Check if secure storage is available
   */
  get isSecureStorage(): boolean {
    return typeof document !== 'undefined';
  }

  /**
   * Get stored tokens
   */
  getTokens(): JWTTokens | null {
    try {
      const accessToken = this.getAccessToken();
      const refreshToken = this.getRefreshToken();

      if (!accessToken || !refreshToken) {
        return null;
      }

      const payload = this.decodeToken(accessToken);
      if (!payload) {
        return null;
      }

      return {
        accessToken,
        refreshToken,
        expiresAt: payload.exp * 1000, // Convert to milliseconds
        tokenType: 'Bearer',
      };
    } catch (error) {
      console.error('Error getting tokens:', error);
      return null;
    }
  }

  /**
   * Store tokens securely
   */
  setTokens(tokens: JWTTokens): void {
    try {
      if (SECURITY_CONFIG.enableTokenEncryption) {
        // TODO: Implement token encryption
        this.setAccessToken(tokens.accessToken);
        this.setRefreshToken(tokens.refreshToken);
      } else {
        this.setAccessToken(tokens.accessToken);
        this.setRefreshToken(tokens.refreshToken);
      }
    } catch (error) {
      console.error('Error storing tokens:', error);
      throw new Error('Failed to store authentication tokens');
    }
  }

  /**
   * Clear all stored tokens
   */
  clearTokens(): void {
    try {
      storage.remove(this.accessTokenKey);
      storage.remove(this.refreshTokenKey);
      
      // Clear from cookies if using secure storage
      if (this.isSecureStorage) {
        this.clearCookie(this.accessTokenKey);
        this.clearCookie(this.refreshTokenKey);
      }
    } catch (error) {
      console.error('Error clearing tokens:', error);
    }
  }

  /**
   * Get access token
   */
  getAccessToken(): string | null {
    const fromCookie = this.getCookie(this.accessTokenKey);
    if (fromCookie) return fromCookie;
    // Legacy migration: if token exists in localStorage, move to cookie and clear storage
    const legacy = storage.get<string>(this.accessTokenKey);
    if (legacy) {
      this.setAccessToken(legacy);
      storage.remove(this.accessTokenKey);
      return legacy;
    }
    return null;
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    const fromCookie = this.getCookie(this.refreshTokenKey);
    if (fromCookie) return fromCookie;
    // Legacy migration: if token exists in localStorage, move to cookie and clear storage
    const legacy = storage.get<string>(this.refreshTokenKey);
    if (legacy) {
      this.setRefreshToken(legacy);
      storage.remove(this.refreshTokenKey);
      return legacy;
    }
    return null;
  }

  /**
   * Set access token
   */
  private setAccessToken(token: string): void {
    this.setCookie(this.accessTokenKey, token, this.getTokenExpiry(token));
  }

  /**
   * Set refresh token
   */
  private setRefreshToken(token: string): void {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30); // 30 days
    this.setCookie(this.refreshTokenKey, token, expiry);
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token?: string): boolean {
    try {
      const tokenToCheck = token || this.getAccessToken();
      if (!tokenToCheck) return true;

      const payload = this.decodeToken(tokenToCheck);
      if (!payload) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp <= currentTime;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  }

  /**
   * Check if token needs refresh (within threshold)
   */
  needsRefresh(token?: string): boolean {
    try {
      const tokenToCheck = token || this.getAccessToken();
      if (!tokenToCheck) return true;

      const payload = this.decodeToken(tokenToCheck);
      if (!payload) return true;

      const currentTime = Date.now();
      const expiryTime = payload.exp * 1000;
      const timeUntilExpiry = expiryTime - currentTime;

      return timeUntilExpiry <= JWT_CONFIG.refreshThreshold;
    } catch (error) {
      console.error('Error checking refresh need:', error);
      return true;
    }
  }

  /**
   * Decode JWT token payload
   */
  decodeToken(token: string): TokenPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded) as TokenPayload;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Get token expiry date
   */
  private getTokenExpiry(token: string): Date {
    const payload = this.decodeToken(token);
    if (payload) {
      return new Date(payload.exp * 1000);
    }
    // Default to 1 hour if can't decode
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 1);
    return expiry;
  }

  /**
   * Refresh tokens using refresh token
   */
  async refreshTokens(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        // Silently fail if no refresh token, as this is expected when session expires
        return false;
      }

      // Import BFF API client dynamically to avoid circular dependency
      const { apiClientBff } = await import('../apiBff');
      const { API_ENDPOINTS } = await import('../../config/api');

      // Call BFF /auth/refresh; upstream expects empty body and Authorization header
      const response = await apiClientBff.post<RefreshTokenResponse>(
        API_ENDPOINTS.AUTH.REFRESH,
        undefined,
        { skipAuth: true, headers: { Authorization: `${JWT_CONFIG.tokenPrefix} ${refreshToken}` } }
      );

      const newAccessToken = response.token || response.accessToken;
      if (!newAccessToken) {
        throw new Error('Invalid refresh response: missing token');
      }

      // Compute expiry from JWT payload if expiresIn not provided
      const payload = this.decodeToken(newAccessToken);
      const expiresAt = payload ? payload.exp * 1000 : (Date.now() + (response.expiresIn ? response.expiresIn * 1000 : 3600 * 1000));

      const currentRefresh = this.getRefreshToken();
      const newTokens: JWTTokens = {
        accessToken: newAccessToken,
        refreshToken: response.refreshToken || currentRefresh || newAccessToken,
        expiresAt,
        tokenType: 'Bearer',
      };

      this.setTokens(newTokens);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Cookie management methods
   */
  private setCookie(name: string, value: string, expires: Date): void {
    const secure = SECURITY_CONFIG.secureCookies ? '; Secure' : '';
    const sameSite = '; SameSite=Strict';
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/${secure}${sameSite}`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    
    return null;
  }

  private clearCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }

  /**
   * Identity & user cookie helpers
   */
  setIdentityEmail(email: string | undefined | null): void {
    const value = email || '';
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    this.setCookie('identityEmail', value, expiry);
    // Remove legacy localStorage
    storage.remove('email');
  }

  getIdentityEmail(): string | null {
    const fromCookie = this.getCookie('identityEmail');
    if (fromCookie) return fromCookie;
    // Legacy migration
    const legacy = storage.get<string>('email');
    if (legacy) {
      this.setIdentityEmail(legacy);
      storage.remove('email');
      return legacy;
    }
    return null;
  }

  clearIdentityEmail(): void {
    this.clearCookie('identityEmail');
  }

  setUsername(username: string | undefined | null): void {
    const value = username || '';
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    this.setCookie('username', value, expiry);
    storage.remove('username');
  }

  getUsername(): string | null {
    const fromCookie = this.getCookie('username');
    if (fromCookie) return fromCookie;
    const legacy = storage.get<string>('username');
    if (legacy) {
      this.setUsername(legacy);
      storage.remove('username');
      return legacy;
    }
    return null;
  }

  clearUsername(): void {
    this.clearCookie('username');
  }

  setAuthUserCookie(user: AuthUser | undefined | null): void {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + 30);
    // Store as URL-encoded JSON for light obfuscation (not encryption)
    const value = user ? encodeURIComponent(JSON.stringify(user)) : '';
    this.setCookie('authUser', value, expiry);
    storage.remove('authUser');
  }

  getAuthUserCookie(): AuthUser | null {
    const raw = this.getCookie('authUser');
    if (raw) {
      try {
        return JSON.parse(raw) as AuthUser;
      } catch {
        // Backward compatibility: try URL-decoding then parse
        try {
          const decoded = decodeURIComponent(raw);
          return JSON.parse(decoded) as AuthUser;
        } catch {
          return null;
        }
      }
    }
    const legacyRaw = storage.get<string>('authUser');
    if (legacyRaw) {
      try {
        const parsed = JSON.parse(legacyRaw) as AuthUser;
        this.setAuthUserCookie(parsed);
        storage.remove('authUser');
        return parsed;
      } catch {
        storage.remove('authUser');
        return null;
      }
    }
    return null;
  }

  clearAuthUserCookie(): void {
    this.clearCookie('authUser');
  }

  /**
   * One-shot migration from legacy localStorage to cookies
   */
  migrateLegacyStorageToCookies(): void {
    try {
      // Tokens
      const legacyAccess = storage.get<string>(this.accessTokenKey);
      if (legacyAccess) {
        this.setAccessToken(legacyAccess);
        storage.remove(this.accessTokenKey);
      }
      const legacyRefresh = storage.get<string>(this.refreshTokenKey);
      if (legacyRefresh) {
        this.setRefreshToken(legacyRefresh);
        storage.remove(this.refreshTokenKey);
      }

      // Identity: stop creating separate cookies; clear any existing ones
      const legacyEmail = storage.get<string>('email');
      if (legacyEmail) storage.remove('email');
      const legacyUsername = storage.get<string>('username');
      if (legacyUsername) storage.remove('username');
      // Proactively clear old cookies if present
      this.clearIdentityEmail();
      this.clearUsername();

      const legacyAuthUser = storage.get<string>('authUser');
      if (legacyAuthUser) {
        try {
          const parsed = JSON.parse(legacyAuthUser) as AuthUser;
          this.setAuthUserCookie(parsed);
        } catch {
          storage.remove('authUser');
        }
      }
    } catch (e) {
      console.warn('Legacy storage migration failed:', e);
    }
  }

  /**
   * Validate token format and signature (basic validation)
   */
  validateTokenFormat(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      // Try to decode header and payload
      const header = JSON.parse(atob(parts[0]));
      const payload = JSON.parse(atob(parts[1]));

      // Basic validation
      return (
        header.typ === 'JWT' &&
        header.alg &&
        payload.sub &&
        payload.exp &&
        payload.iat
      );
    } catch (error) {
      return false;
    }
  }

  /**
   * Get time until token expires (in milliseconds)
   */
  getTimeUntilExpiry(token?: string): number {
    try {
      const tokenToCheck = token || this.getAccessToken();
      if (!tokenToCheck) return 0;

      const payload = this.decodeToken(tokenToCheck);
      if (!payload) return 0;

      const currentTime = Date.now();
      const expiryTime = payload.exp * 1000;
      
      return Math.max(0, expiryTime - currentTime);
    } catch (error) {
      return 0;
    }
  }
}

// Create singleton instance
export const tokenService = new TokenService();

// Export class for testing
export { TokenService };

// Export utility functions
export const tokenUtils = {
  isExpired: (token: string) => tokenService.isTokenExpired(token),
  decode: (token: string) => tokenService.decodeToken(token),
  needsRefresh: (token: string) => tokenService.needsRefresh(token),
  validate: (token: string) => tokenService.validateTokenFormat(token),
  timeUntilExpiry: (token: string) => tokenService.getTimeUntilExpiry(token),
};

export default tokenService;